/**
 * @fileoverview Hook for subscribing to legacy migration grid data and receipts.
 * @module system-web/hooks/useLegacyMigrationGrid
 *
 * Responsibilities:
 * - Subscribe to migration receipts via Dexie liveQuery.
 * - Fetch live SQL grid rows via fetchLiveLegacyGrid on mount / dep change.
 * - Merge receipt overlay onto live grid rows.
 * - Manage server-side pagination state (page, pageSize).
 * - Expose a refetch callback for manual refresh.
 *
 * Dependencies:
 * - dexie (liveQuery)
 * - @firesquirrel/local-db
 * - ../services/legacyMigrationService
 *
 * Architecture:
 * - Receipts are local-first: liveQuery on `legacy_migration_receipts`.
 * - Live SQL rows are online-exception: fetched in a separate useEffect, stored in component state.
 * - The two are merged reactively: whenever receipts change, re-overlay.
 * - liveQuery observed function performs ONLY reads — no writes, no API calls (§liveQuery contract).
 * - Pagination is server-side: each page change triggers a new API request.
 *
 * Notes:
 * - fetchLiveLegacyGrid is called from useEffect, never inside liveQuery (§4.3 of impl plan).
 * - Receipt overlay attaches a `migratedReceipt` to each ScoredGridRow where the legacySourceId matches.
 * - When tenantId or datasetKey changes, the live rows are cleared and re-fetched.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { liveQuery } from 'dexie';
import { getSystemDb } from '@firesquirrel/local-db';
import {
  listReceipts,
  fetchLiveLegacyGrid,
  type MigrationReceiptRow,
  type ScoredGridRow,
} from '../services/legacyMigrationService';

/** Default page size matching other grids in the codebase. */
const DEFAULT_PAGE_SIZE = 50;

/**
 * @contract LegacyMigrationGridState
 *
 * Purpose:
 * - Shape returned by useLegacyMigrationGrid.
 *
 * Fields:
 * - rows: Live SQL rows merged with receipt overlays (online-exception data + Dexie overlay).
 * - receipts: Raw receipt rows from Dexie liveQuery.
 * - isFetching: True while fetchLiveLegacyGrid is in-flight.
 * - fetchError: Error message when the live fetch fails.
 * - refetch: Callback to trigger a manual re-fetch of live SQL rows.
 * - total: Total number of scored rows across all pages.
 * - page: Current 1-based page index.
 * - pageSize: Number of rows per page.
 * - pageCount: Total number of pages (derived from total / pageSize).
 * - setPage: Navigate to a specific page (1-based).
 * - setPageSize: Change the number of rows per page (resets to page 1).
 * - physicalDatabase: The actual Streamline database name the server queried (e.g.
 *   'Dev_SLInspections001'), distinct from the logical dataset key in non-prod environments.
 * - appEnv: The server's deployment environment ('dev' | 'stage' | 'prod') for the last fetch.
 *
 * Rules:
 * - rows is [] before first fetch, or while tenantId/datasetKey is unset.
 * - migratedReceipt is undefined on rows that have not yet been migrated.
 *
 * Sync:
 * - rows reflects the latest live SQL fetch + latest Dexie receipt state.
 */
export interface LegacyMigrationGridState {
  rows: ScoredGridRow[];
  receipts: MigrationReceiptRow[];
  isFetching: boolean;
  fetchError: string | null;
  refetch: () => void;
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  bandFilter: string;
  setBandFilter: (band: string) => void;
  sortBy: string;
  sortDir: 'asc' | 'desc';
  setSort: (columnKey: string) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  columnFilters: Record<string, string>;
  setColumnFilters: (filters: Record<string, string>) => void;
  queryJson: string;
  setQueryJson: (query: string) => void;
  unmetDeps: string[];
  perTableCounts: Record<string, number>;
  physicalDatabase: string | null;
  appEnv: string | null;
}

/**
 * @hook useLegacyMigrationGrid
 *
 * Purpose:
 * - Provide reactive merged grid data: live SQL rows + Dexie receipt overlays.
 * - Manage server-side pagination state.
 *
 * Returns:
 * - rows, receipts, isFetching, fetchError, refetch, total, page, pageSize, pageCount, setPage, setPageSize
 *
 * Side Effects:
 * - Subscribes a Dexie liveQuery for receipts (torn down on unmount / dep change).
 * - Fires fetchLiveLegacyGrid on mount and when tenantId, datasetKey, page, or pageSize changes.
 * - refetch() also re-reads Dexie receipts so Migrated overlays update without remounting.
 *
 * Offline Behavior:
 * - Receipt overlays remain available offline (Dexie).
 * - Live grid rows cannot be loaded offline (online exception — SQL fetch).
 */
export function useLegacyMigrationGrid(
  tenantId: string | null,
  datasetKey: string | null,
): LegacyMigrationGridState {
  const [rawRows, setRawRows] = useState<ScoredGridRow[]>([]);
  const [receipts, setReceipts] = useState<MigrationReceiptRow[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPageState] = useState(1);
  const [pageSize, setPageSizeState] = useState(DEFAULT_PAGE_SIZE);
  const [bandFilter, setBandFilterState] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [searchQuery, setSearchQueryState] = useState<string>('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>('');
  const [columnFilters, setColumnFiltersState] = useState<Record<string, string>>({});
  const [debouncedColumnFilters, setDebouncedColumnFilters] = useState<Record<string, string>>({});
  const [queryJson, setQueryJsonState] = useState<string>('');
  const [unmetDeps, setUnmetDeps] = useState<string[]>([]);
  const [perTableCounts, setPerTableCounts] = useState<Record<string, number>>({});
  const [physicalDatabase, setPhysicalDatabase] = useState<string | null>(null);
  const [appEnv, setAppEnv] = useState<string | null>(null);

  // Stable refetch trigger — incrementing this value re-fires the fetch useEffect
  // and re-reads Dexie receipts (putMany often does not retrigger liveQuery).
  const [fetchTick, setFetchTick] = useState(0);
  const refetch = useCallback(() => setFetchTick((t) => t + 1), []);

  const setBandFilter = useCallback((band: string) => {
    setBandFilterState(band);
    setPageState(1);
  }, []);

  const setSearchQuery = useCallback((query: string) => {
    setSearchQueryState(query);
    setPageState(1);
  }, []);

  const setColumnFilters = useCallback((filters: Record<string, string>) => {
    setColumnFiltersState(filters);
    setPageState(1);
  }, []);

  const setQueryJson = useCallback((query: string) => {
    setQueryJsonState(query);
    setPageState(1);
  }, []);

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearchQuery(searchQuery), 300);
    return () => clearTimeout(handle);
  }, [searchQuery]);

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedColumnFilters(columnFilters), 300);
    return () => clearTimeout(handle);
  }, [columnFilters]);

  const setSort = useCallback((columnKey: string) => {
    if (sortBy === columnKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(columnKey);
      setSortDir(columnKey === 'score' ? 'asc' : 'asc');
    }
    setPageState(1);
  }, [sortBy]);

  // Page navigation: clamp to valid range.
  const setPage = useCallback((p: number) => {
    setPageState(Math.max(1, p));
  }, []);

  // Page size change: reset to page 1.
  const setPageSize = useCallback((size: number) => {
    setPageSizeState(Math.max(1, Math.min(200, size)));
    setPageState(1);
  }, []);

  // Reset pagination when tenant or dataset changes.
  useEffect(() => {
    setPageState(1);
  }, [tenantId, datasetKey]);

  // Unmount guard to prevent setState on unmounted components.
  const unmountedRef = useRef(false);
  useEffect(() => {
    unmountedRef.current = false;
    return () => { unmountedRef.current = true; };
  }, []);

  // liveQuery subscription for receipts — pure read, no writes.
  useEffect(() => {
    if (!tenantId || !datasetKey) {
      setReceipts([]);
      return;
    }

    let subscription: { unsubscribe: () => void } | null = null;
    let cancelled = false;

    getSystemDb()
      .then((db) => {
        if (cancelled) return;
        subscription = liveQuery(
          () => listReceipts(tenantId, datasetKey),
        ).subscribe({
          next: (rows) => {
            if (!cancelled && !unmountedRef.current) setReceipts(rows);
          },
          error: (err: unknown) => {
            if (!cancelled && !unmountedRef.current) {
              console.warn('[useLegacyMigrationGrid] liveQuery receipts error:', err);
            }
          },
        });
        // Suppress unused variable warning — db is not used directly but the
        // import guarantees the DB is open before liveQuery runs.
        void db;
      })
      .catch((err) => {
        if (!cancelled && !unmountedRef.current) {
          console.warn('[useLegacyMigrationGrid] getSystemDb error:', err);
        }
      });

    return () => {
      cancelled = true;
      subscription?.unsubscribe();
    };
  }, [tenantId, datasetKey]);

  useEffect(() => {
    if (fetchTick === 0 || !tenantId || !datasetKey) return;
    let cancelled = false;
    void listReceipts(tenantId, datasetKey).then((rows) => {
      if (!cancelled && !unmountedRef.current) setReceipts(rows);
    }).catch((err: unknown) => {
      if (!cancelled && !unmountedRef.current) {
        console.warn('[useLegacyMigrationGrid] receipt refetch error:', err);
      }
    });
    return () => { cancelled = true; };
  }, [fetchTick, tenantId, datasetKey]);

  // Online-exception fetch — fires on mount and when deps/pagination change.
  // Must NOT be inside liveQuery (§liveQuery contract).
  useEffect(() => {
    if (!tenantId || !datasetKey) {
      setRawRows([]);
      setFetchError(null);
      setTotal(0);
      return;
    }

    let cancelled = false;
    setIsFetching(true);
    setFetchError(null);

    fetchLiveLegacyGrid(tenantId, datasetKey, page, pageSize, bandFilter, sortBy, sortDir, debouncedSearchQuery, debouncedColumnFilters, queryJson)
      .then((result) => {
        if (!cancelled && !unmountedRef.current) {
          setRawRows(result.rows);
          setTotal(result.total);
          setUnmetDeps(result.unmetDeps || []);
          setPerTableCounts(result.perTableCounts ?? {});
          setPhysicalDatabase(result.physicalDatabase ?? null);
          setAppEnv(result.appEnv ?? null);
          setIsFetching(false);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled && !unmountedRef.current) {
          const msg = err instanceof Error ? err.message : 'Failed to fetch legacy grid data';
          console.error('[useLegacyMigrationGrid] fetchLiveLegacyGrid failed:', err);
          setFetchError(msg);
          setIsFetching(false);
        }
      });

    return () => { cancelled = true; };
  }, [tenantId, datasetKey, page, pageSize, bandFilter, sortBy, sortDir, debouncedSearchQuery, debouncedColumnFilters, queryJson, fetchTick]);

  // Merge receipt overlay onto raw rows reactively.
  const receiptMap = new Map(receipts.map((r) => [r.legacySourceId, r]));
  const rows: ScoredGridRow[] = rawRows.map((row) => ({
    ...row,
    migratedReceipt: receiptMap.get(row.legacyId) ?? row.migratedReceipt,
  }));

  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  return {
    rows,
    receipts,
    isFetching,
    fetchError,
    refetch,
    total,
    page,
    pageSize,
    pageCount,
    setPage,
    setPageSize,
    bandFilter,
    setBandFilter,
    sortBy,
    sortDir,
    setSort,
    searchQuery,
    setSearchQuery,
    columnFilters,
    setColumnFilters,
    queryJson,
    setQueryJson,
    unmetDeps,
    perTableCounts,
    physicalDatabase,
    appEnv,
  };
}
